/**
 * Quality validation service for GenFlappy
 *
 * Validates AI-generated assets for grayscale compliance, dimension accuracy,
 * and edge transparency. Assets must achieve a weighted score of ≥80 to pass.
 *
 * @module services/qualityValidation
 */

import type {
  AssetType,
  ValidationResult,
  DimensionTarget,
} from '../types/validation';
import {
  VALIDATION_THRESHOLDS,
  TARGET_DIMENSIONS,
} from '../types/validation';
import { detectQualityFailure, logError } from './errorDetectionService';

/**
 * Load an image from a URL and extract ImageData for pixel analysis
 *
 * Uses OffscreenCanvas for better performance when available, with fallback
 * to regular Canvas for compatibility. Loads the image, draws it to canvas,
 * and extracts raw pixel data via getImageData().
 *
 * @param {string} imageUrl - Base64 data URL or absolute URL to image
 * @returns {Promise<ImageData>} Raw pixel data with RGBA values
 * @throws {Error} If image fails to load or canvas is not supported
 *
 * @example
 * ```typescript
 * const imageData = await loadImageToCanvas('data:image/png;base64,...');
 * // imageData.data contains RGBA pixel array
 * ```
 */
export async function loadImageToCanvas(imageUrl: string): Promise<ImageData> {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      try {
        const width = img.naturalWidth || img.width;
        const height = img.naturalHeight || img.height;

        // Use OffscreenCanvas if available (better performance)
        if (typeof OffscreenCanvas !== 'undefined') {
          const canvas = new OffscreenCanvas(width, height);
          const ctx = canvas.getContext('2d');

          if (!ctx) {
            throw new Error('Failed to get OffscreenCanvas 2D context');
          }

          ctx.drawImage(img, 0, 0);
          const imageData = ctx.getImageData(0, 0, width, height);
          resolve(imageData);
        } else {
          // Fallback to regular Canvas
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');

          if (!ctx) {
            throw new Error('Failed to get Canvas 2D context');
          }

          ctx.drawImage(img, 0, 0);
          const imageData = ctx.getImageData(0, 0, width, height);
          resolve(imageData);
        }
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      reject(new Error(`Failed to load image from URL: ${imageUrl.substring(0, 50)}...`));
    };

    // Set crossOrigin to handle CORS if needed
    img.crossOrigin = 'anonymous';

    // Trigger image load
    img.src = imageUrl;
  });
}

/**
 * Validate grayscale compliance of an asset
 *
 * Calculates RGB deviation for each pixel to detect color presence.
 * Pure grayscale has R=G=B. Deviation formula: abs(R-G) + abs(G-B) + abs(R-B).
 * Skips fully transparent pixels (alpha <10) to avoid false positives.
 *
 * @param {ImageData} imageData - Raw pixel data from canvas
 * @returns {{ score: number; deviation: number }} Score (0-100) and deviation percentage
 *
 * Score calculation:
 * - 0% deviation = 100 score (perfect grayscale)
 * - 15% deviation = 0 score (threshold)
 * - >15% deviation = 0 score (failed)
 *
 * @example
 * ```typescript
 * const result = validateGrayscale(imageData);
 * // { score: 85, deviation: 3.2 }
 * ```
 */
export function validateGrayscale(imageData: ImageData): {
  score: number;
  deviation: number;
} {
  const { data } = imageData;
  let totalDeviation = 0;
  let processedPixels = 0;

  // Process all pixels
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const a = data[i + 3];

    // Skip fully transparent pixels (alpha <10)
    if (a < 10) {
      continue;
    }

    // Calculate RGB deviation
    // Perfect grayscale: R=G=B, deviation = 0
    // Max deviation: 765 (when R=255, G=0, B=0)
    const deviation = Math.abs(r - g) + Math.abs(g - b) + Math.abs(r - b);
    totalDeviation += deviation;
    processedPixels++;
  }

  // Handle edge case: all pixels transparent
  if (processedPixels === 0) {
    return { score: 100, deviation: 0 };
  }

  // Calculate average deviation
  const avgDeviation = totalDeviation / processedPixels;

  // Convert to percentage (max deviation is 765)
  const deviationPercent = (avgDeviation / 765) * 100;

  // Calculate score (0-100)
  // 0% deviation = 100 score
  // 15% deviation = 0 score
  // Linear scaling with 6.67 multiplier (100 / 15)
  const score = Math.max(0, 100 - deviationPercent * 6.67);

  return {
    score: Math.round(score * 100) / 100, // Round to 2 decimals
    deviation: Math.round(deviationPercent * 100) / 100,
  };
}

/**
 * Validate dimension accuracy of an asset
 *
 * Compares actual dimensions against target dimensions for the asset type.
 * Allows ±5% tolerance for both width and height. Uses maximum deviation
 * of the two dimensions for scoring.
 *
 * @param {HTMLImageElement} image - Loaded image element
 * @param {AssetType} assetType - Type of asset (character, obstacle, background)
 * @returns {{ score: number; widthDeviation: number; heightDeviation: number }}
 *
 * Score calculation:
 * - 0% deviation = 100 score (exact match)
 * - 5% deviation = 0 score (threshold)
 * - >5% deviation = 0 score (failed)
 *
 * @example
 * ```typescript
 * const result = validateDimensions(img, 'character');
 * // { score: 60, widthDeviation: 2.3, heightDeviation: 1.8 }
 * ```
 */
export function validateDimensions(
  image: HTMLImageElement,
  assetType: AssetType
): {
  score: number;
  widthDeviation: number;
  heightDeviation: number;
} {
  const actualWidth = image.naturalWidth || image.width;
  const actualHeight = image.naturalHeight || image.height;

  const target: DimensionTarget = TARGET_DIMENSIONS[assetType];

  // Calculate deviation percentages
  const widthDeviation =
    (Math.abs(actualWidth - target.width) / target.width) * 100;
  const heightDeviation =
    (Math.abs(actualHeight - target.height) / target.height) * 100;

  // Use maximum deviation for scoring (both must be within tolerance)
  const maxDeviation = Math.max(widthDeviation, heightDeviation);

  // Calculate score (0-100)
  // 0% deviation = 100 score
  // 5% deviation = 0 score
  // Linear scaling with 20 multiplier (100 / 5)
  const score = Math.max(0, 100 - maxDeviation * 20);

  return {
    score: Math.round(score * 100) / 100,
    widthDeviation: Math.round(widthDeviation * 100) / 100,
    heightDeviation: Math.round(heightDeviation * 100) / 100,
  };
}

/**
 * Generate human-readable failure reason for dimension validation
 *
 * @param {number} actualWidth - Actual image width
 * @param {number} actualHeight - Actual image height
 * @param {DimensionTarget} target - Target dimensions
 * @returns {string} Formatted failure message
 *
 * @example
 * ```typescript
 * const reason = generateDimensionFailureReason(130, 130, { width: 128, height: 128 });
 * // "Dimensions 130x130px (expected 128x128px ±5%)"
 * ```
 */
export function generateDimensionFailureReason(
  actualWidth: number,
  actualHeight: number,
  target: DimensionTarget
): string {
  return `Dimensions ${actualWidth}x${actualHeight}px (expected ${target.width}x${target.height}px ±5%)`;
}

/**
 * Validate edge transparency of an asset
 *
 * Samples edge pixels (top/bottom rows, left/right columns) and counts
 * near-transparent pixels (alpha <10). Requires >80% edge transparency.
 * Optimizes large images by sampling every 10th pixel.
 *
 * @param {ImageData} imageData - Raw pixel data from canvas
 * @returns {{ score: number; transparency: number }} Score (0-100) and transparency %
 *
 * Score calculation:
 * - 100% transparency = 100 score
 * - 80% transparency = 100 score (threshold, capped)
 * - 60% transparency = 75 score
 * - 0% transparency = 0 score
 *
 * @example
 * ```typescript
 * const result = validateTransparency(imageData);
 * // { score: 100, transparency: 95.2 }
 * ```
 */
export function validateTransparency(imageData: ImageData): {
  score: number;
  transparency: number;
} {
  const { data, width, height } = imageData;

  // Determine if this is a large image (>500k pixels)
  const isLargeImage = width * height > 500000;
  const sampleStep = isLargeImage ? 10 : 1; // Sample every 10th pixel for large images

  let edgePixels = 0;
  let transparentEdgePixels = 0;

  // Sample top row
  for (let x = 0; x < width; x += sampleStep) {
    const i = (0 * width + x) * 4;
    const alpha = data[i + 3];
    edgePixels++;
    if (alpha < 10) transparentEdgePixels++;
  }

  // Sample bottom row
  for (let x = 0; x < width; x += sampleStep) {
    const i = ((height - 1) * width + x) * 4;
    const alpha = data[i + 3];
    edgePixels++;
    if (alpha < 10) transparentEdgePixels++;
  }

  // Sample left column (excluding corners already sampled)
  for (let y = 1; y < height - 1; y += sampleStep) {
    const i = (y * width + 0) * 4;
    const alpha = data[i + 3];
    edgePixels++;
    if (alpha < 10) transparentEdgePixels++;
  }

  // Sample right column (excluding corners already sampled)
  for (let y = 1; y < height - 1; y += sampleStep) {
    const i = (y * width + (width - 1)) * 4;
    const alpha = data[i + 3];
    edgePixels++;
    if (alpha < 10) transparentEdgePixels++;
  }

  // Calculate transparency percentage
  const transparencyPercent =
    edgePixels > 0 ? (transparentEdgePixels / edgePixels) * 100 : 0;

  // Calculate score (0-100)
  // 80%+ transparency = 100 score (capped at threshold)
  // Below 80% = proportional score
  const score = Math.min(100, transparencyPercent * 1.25);

  return {
    score: Math.round(score * 100) / 100,
    transparency: Math.round(transparencyPercent * 100) / 100,
  };
}

/**
 * Calculate overall quality score from individual validation scores
 *
 * Applies weighted average:
 * - Grayscale: 40% (most critical for theme consistency)
 * - Dimension: 30% (important for gameplay collision)
 * - Transparency: 30% (important for visual quality)
 *
 * @param {number} grayscaleScore - Grayscale validation score (0-100)
 * @param {number} dimensionScore - Dimension validation score (0-100)
 * @param {number} transparencyScore - Transparency validation score (0-100)
 * @param {number} grayscaleDeviation - Grayscale deviation percentage
 * @param {number} widthDeviation - Width deviation percentage
 * @param {number} heightDeviation - Height deviation percentage
 * @param {number} transparencyPercent - Edge transparency percentage
 * @param {number} actualWidth - Actual image width
 * @param {number} actualHeight - Actual image height
 * @param {AssetType} assetType - Asset type for target dimensions
 * @returns {Omit<ValidationResult, 'validationDurationMs'>} Validation result without duration
 *
 * @example
 * ```typescript
 * const result = calculateQualityScore(95, 88, 100, 2.5, 1.2, 0.8, 98.5, 128, 128, 'character');
 * // { overallScore: 93.9, passed: true, ... }
 * ```
 */
export function calculateQualityScore(
  grayscaleScore: number,
  dimensionScore: number,
  transparencyScore: number,
  grayscaleDeviation: number,
  widthDeviation: number,
  heightDeviation: number,
  transparencyPercent: number,
  actualWidth: number,
  actualHeight: number,
  assetType: AssetType
): Omit<ValidationResult, 'validationDurationMs'> {
  // Calculate weighted overall score
  // Backgrounds don't need transparency, so adjust weights accordingly
  const overallScore = assetType === 'background'
    ? grayscaleScore * 0.5 + dimensionScore * 0.5  // No transparency weight for backgrounds
    : grayscaleScore * 0.4 + dimensionScore * 0.3 + transparencyScore * 0.3;

  // Determine pass/fail
  const passed = overallScore >= VALIDATION_THRESHOLDS.OVERALL_PASSING_SCORE;

  // Generate failure reasons
  const failureReasons: string[] = [];

  if (grayscaleDeviation > VALIDATION_THRESHOLDS.GRAYSCALE_DEVIATION_MAX) {
    failureReasons.push(
      `Grayscale deviation ${grayscaleDeviation.toFixed(1)}% (threshold ${VALIDATION_THRESHOLDS.GRAYSCALE_DEVIATION_MAX}%)`
    );
  }

  const maxDimensionDeviation = Math.max(widthDeviation, heightDeviation);
  if (maxDimensionDeviation > VALIDATION_THRESHOLDS.DIMENSION_TOLERANCE) {
    const target = TARGET_DIMENSIONS[assetType];
    failureReasons.push(
      generateDimensionFailureReason(actualWidth, actualHeight, target)
    );
  }

  // Only check transparency for character and obstacle assets (backgrounds should be opaque)
  if (assetType !== 'background' && transparencyPercent < VALIDATION_THRESHOLDS.TRANSPARENCY_MIN) {
    failureReasons.push(
      `Transparency ${transparencyPercent.toFixed(1)}% (threshold ${VALIDATION_THRESHOLDS.TRANSPARENCY_MIN}%)`
    );
  }

  return {
    overallScore: Math.round(overallScore * 100) / 100,
    passed,
    grayscaleScore: Math.round(grayscaleScore * 100) / 100,
    dimensionScore: Math.round(dimensionScore * 100) / 100,
    transparencyScore: Math.round(transparencyScore * 100) / 100,
    failureReasons,
  };
}

/**
 * Validate asset quality (main entry point)
 *
 * Runs complete validation pipeline: loads image to canvas, performs
 * grayscale/dimension/transparency checks, calculates weighted score.
 * Assets must score ≥80 to pass validation.
 *
 * Performance targets (NFR36):
 * - Grayscale: <200ms for 128x128px
 * - Dimension: <50ms
 * - Transparency: <200ms
 * - Total: <500ms per asset
 *
 * @param {string} imageUrl - Base64 data URL or absolute URL
 * @param {AssetType} assetType - Type of asset to validate
 * @returns {Promise<ValidationResult>} Complete validation result with scores
 *
 * @example
 * ```typescript
 * const result = await validateAsset('data:image/png;base64,...', 'character');
 * if (result.passed) {
 *   console.log('Asset passed validation!');
 * } else {
 *   console.log('Failures:', result.failureReasons);
 * }
 * ```
 */
export async function validateAsset(
  imageUrl: string,
  assetType: AssetType
): Promise<ValidationResult> {
  const startTime = performance.now();

  try {
    // Load image to canvas and extract pixel data
    const imageData = await loadImageToCanvas(imageUrl);

    // Create image element for dimension checking
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Failed to load image for dimension check'));
      img.src = imageUrl;
    });

    // Run all validation checks
    const grayscaleResult = validateGrayscale(imageData);
    const dimensionResult = validateDimensions(img, assetType);
    const transparencyResult = validateTransparency(imageData);

    // Calculate overall quality score
    const qualityScore = calculateQualityScore(
      grayscaleResult.score,
      dimensionResult.score,
      transparencyResult.score,
      grayscaleResult.deviation,
      dimensionResult.widthDeviation,
      dimensionResult.heightDeviation,
      transparencyResult.transparency,
      img.naturalWidth || img.width,
      img.naturalHeight || img.height,
      assetType
    );

    // Calculate validation duration
    const validationDurationMs = Math.round(performance.now() - startTime);

    // Log slow validations
    if (validationDurationMs > 500) {
      console.warn(`Slow quality validation: ${validationDurationMs}ms for ${assetType}`);
    }

    // Story 6.1: Detect and log quality failures
    if (!qualityScore.passed) {
      const errorContext = detectQualityFailure(assetType, qualityScore.overallScore / 100);
      if (errorContext) {
        logError({
          ...errorContext,
          demoModeActivated: 'none', // Will be determined by orchestration layer
        });
      }
    }

    return {
      ...qualityScore,
      validationDurationMs,
    };
  } catch (error) {
    // Handle errors gracefully
    console.error('Quality validation failed:', error);

    const validationDurationMs = Math.round(performance.now() - startTime);

    // Return failed validation result
    return {
      overallScore: 0,
      passed: false,
      grayscaleScore: 0,
      dimensionScore: 0,
      transparencyScore: 0,
      failureReasons: [
        `Validation error: ${error instanceof Error ? error.message : 'Unknown error'}`,
      ],
      validationDurationMs,
    };
  }
}
